import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { APPLICATION_STATUS_LABELS, APPLICATION_STATUS_TONES, StatusPill } from "@/components/StatusPill";
import { formatMoney } from "@/utils/quoteTotals";
import { formatDate } from "@/utils/dates";
import { toastApiError } from "@/lib/toast";
import {
  equipment,
  EQUIPMENT_NEED_LABELS,
  EQUIPMENT_STATUS_LABELS,
  EQUIPMENT_STATUS_TONES,
  type EquipmentOverview,
} from "@/features/equipment/api/equipment.api";
import { ApplicationPicker } from "@/features/equipment/components/ApplicationPicker";

type View = "needs" | "orders";

function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return <TableHead className={`text-xs font-medium uppercase tracking-wide ${className ?? ""}`}>{children}</TableHead>;
}

export function EquipmentPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<EquipmentOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("needs");
  const [picking, setPicking] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await equipment.overview());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load equipment orders.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function start(applicationId: string) {
    setStarting(applicationId);
    try {
      const order = await equipment.start(applicationId);
      navigate(`/equipment/orders/${order.id}`);
    } catch (e) {
      toastApiError(e, "Couldn't start the order.");
    } finally {
      setStarting(null);
    }
  }

  const backOffice = data?.viewerIsBackOffice ?? false;

  return (
    <>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Order Equipment</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {backOffice
              ? "Every equipment order, each linked to its application."
              : "Order hardware and software for your merchants. Each order is linked to one application."}
          </p>
        </div>
        <Button size="sm" onClick={() => setPicking(true)}>
          <Plus className="h-4 w-4" />
          New order
        </Button>
      </header>

      {picking && (
        <ApplicationPicker
          options={data?.needsEquipment ?? []}
          busy={starting !== null}
          onPick={(id) => void start(id)}
          onClose={() => setPicking(false)}
        />
      )}

      {error && <Notice tone="error" className="mb-4">{error}</Notice>}
      {!data && !error && <Loading>Loading…</Loading>}

      {data && (
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList variant="underline" className="mb-4">
            <TabsTrigger variant="underline" value="needs">Needs equipment ({data.needsEquipment.length})</TabsTrigger>
            <TabsTrigger variant="underline" value="orders">
              {backOffice ? "All orders" : "My orders"} ({data.orders.length})
            </TabsTrigger>
          </TabsList>

          {view === "needs" && (
            <Card>
              {data.needsEquipment.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">Every application has its equipment sorted.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <Th>Business</Th>
                      <Th>Application status</Th>
                      {backOffice && <Th>Agent</Th>}
                      <Th className="hidden sm:table-cell">Submitted</Th>
                      <Th />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.needsEquipment.map((a) => (
                      <TableRow key={a.applicationId}>
                        <TableCell>
                          <Link to={`/applications/v2/${a.applicationId}`} className="text-brand-600 hover:underline">
                            {a.applicationDba || "Unnamed business"}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <StatusPill value={a.applicationStatus} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
                        </TableCell>
                        {backOffice && <TableCell>{a.agentName ?? "—"}</TableCell>}
                        <TableCell className="hidden sm:table-cell">{formatDate(a.submittedAt) || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" disabled={starting !== null} onClick={() => void start(a.applicationId)}>
                            {a.draftOrderId ? "Continue order →" : "Order equipment →"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          )}

          {view === "orders" && (
            <Card>
              {data.orders.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">No equipment orders yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <Th>Order</Th>
                      <Th>Business</Th>
                      <Th>Items</Th>
                      {backOffice && <Th>Agent</Th>}
                      <Th className="text-right">Merchant pays</Th>
                      <Th className="text-right">Agent covers</Th>
                      <Th className="hidden sm:table-cell">Placed</Th>
                      <Th>Status</Th>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.orders.map((o) => (
                      <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/equipment/orders/${o.id}`)}>
                        <TableCell className="font-medium">
                          <Link to={`/equipment/orders/${o.id}`} className="text-brand-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                            {o.number}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/applications/v2/${o.applicationId}`}
                            className="text-brand-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {o.applicationDba || "Unnamed business"}
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          <span className="text-muted-foreground">{EQUIPMENT_NEED_LABELS[o.need]} · </span>
                          {o.summary}
                        </TableCell>
                        {backOffice && <TableCell>{o.agentName ?? "—"}</TableCell>}
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(o.merchantDueAtInstall)}
                          <div className="text-xs text-muted-foreground">{formatMoney(o.merchantMonthly)}/mo</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(o.agentCoversOneTime)}
                          {o.agentCoversMonthly > 0 && (
                            <div className="text-xs text-muted-foreground">{formatMoney(o.agentCoversMonthly)}/mo</div>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{formatDate(o.submittedAt ?? o.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            <StatusPill value={o.status} map={EQUIPMENT_STATUS_TONES} labels={EQUIPMENT_STATUS_LABELS} />
                            {backOffice && o.flagged && <StatusPill value="Flagged" fallback="danger" />}
                            {o.applicationClosed && <StatusPill value="Don't ship: application closed" fallback="danger" />}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          )}
        </Tabs>
      )}
    </>
  );
}
